import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, EyeOff } from 'lucide-react';

import { useGetVacancyComplaintQuery } from '@/api/vacancyComplaintsApi';
import { ComplaintSection } from '@/components/vacancy-complaints/ComplaintSection';
import { DismissComplaintModal } from '@/components/vacancy-complaints/DismissComplaintModal';
import { HideVacancyModal } from '@/components/vacancy-complaints/HideVacancyModal';
import { OtherComplaintsSection } from '@/components/vacancy-complaints/OtherComplaintsSection';
import { VacancySection } from '@/components/vacancy-complaints/VacancySection';
import {
    COMPLAINT_STATUS_COLOR,
    COMPLAINT_STATUS_LABEL
} from '@/lib/vacancyComplaintLabels';
import type { VacancyComplaintDetail } from '@/lib/vacancyComplaintsSchemas';

export function VacancyComplaintDetailPage(): ReactElement {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [isHideOpen, setHideOpen] = useState(false);
    const [isDismissOpen, setDismissOpen] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const { data, isLoading, isError } = useGetVacancyComplaintQuery(
        { id: id ?? '' },
        { skip: id === undefined || id === '' }
    );

    if (id === undefined || id === '') {
        return (
            <div className="text-sm text-red-600" data-testid="vacancy-complaint-bad-id">
                Не указан ID жалобы
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка жалобы...</div>;
    }

    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="vacancy-complaint-error">
                Не удалось загрузить жалобу
            </div>
        );
    }

    const complaint: VacancyComplaintDetail = data.complaint;
    const isPending = complaint.status === 'pending';

    return (
        <div data-testid="vacancy-complaint-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/vacancy-complaints')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="vacancy-complaint-back"
                >
                    <ArrowLeft size={16} />
                    К списку
                </button>
                <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${COMPLAINT_STATUS_COLOR[complaint.status]}`}
                    data-testid="vacancy-complaint-status"
                >
                    {COMPLAINT_STATUS_LABEL[complaint.status]}
                </span>
            </div>

            <div className="rounded border border-gray-200 bg-white p-5 space-y-5">
                <ComplaintSection complaint={complaint} />

                <VacancySection vacancy={data.vacancy} />

                <OtherComplaintsSection
                    count={data.otherComplaintsCount}
                    items={data.otherComplaints}
                />
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="vacancy-complaint-action-error"
                >
                    <span>{actionError}</span>
                    <button
                        type="button"
                        onClick={() => setActionError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isPending && (
                <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                    <button
                        type="button"
                        onClick={() => {
                            setActionError(null);
                            setDismissOpen(true);
                        }}
                        data-testid="vacancy-complaint-dismiss-button"
                        className="inline-flex items-center gap-2 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                        <Check size={14} />
                        Без нарушений
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setActionError(null);
                            setHideOpen(true);
                        }}
                        data-testid="vacancy-complaint-hide-button"
                        className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                    >
                        <EyeOff size={14} />
                        Скрыть вакансию
                    </button>
                </div>
            )}

            <HideVacancyModal
                isOpen={isHideOpen}
                complaintId={complaint.id}
                onClose={() => setHideOpen(false)}
                onResolved={() => setHideOpen(false)}
                onError={setActionError}
            />

            <DismissComplaintModal
                isOpen={isDismissOpen}
                complaintId={complaint.id}
                onClose={() => setDismissOpen(false)}
                onResolved={() => setDismissOpen(false)}
                onError={setActionError}
            />
        </div>
    );
}
